'use client';
import React, { useState } from 'react';
import { SessionContext } from '@benzinga/session-context';
import { useActiveWorkspaceSubscriber } from '@benzinga/pro-tools';
import { Security } from '@benzinga/quotes-v3-manager';
import { QuotesV3HoldingsManager, HoldingsQuote } from '@benzinga/quotes-v3-holdings-manager';
import Hooks from '@benzinga/hooks';

export const useQuoteHoldingsSubscription = (
  symbols: Security[],
  fields: Set<keyof HoldingsQuote>,
): HoldingsQuote[] | undefined => {
  const [quotes, setQuotes] = useState<HoldingsQuote[] | undefined>(undefined);
  useQuoteHoldingsSubscriptionCallback(symbols, fields, setQuotes);
  return quotes;
};

export const useQuoteHoldingsSubscriptionCallback = (
  symbols: Security[],
  fields: Set<keyof HoldingsQuote>,
  setter: (quote: HoldingsQuote[]) => void,
) => {
  const session = React.useContext(SessionContext);
  const quotesManager = session.getManager(QuotesV3HoldingsManager);
  const feed = React.useMemo(() => {
    const feed = quotesManager.createQuotesFeed(symbols, true);
    setter(feed.getCachedValue());
    return feed;
  }, [setter, quotesManager, symbols]);

  const [throttledCallback] = Hooks.useThrottle(setter, 1000);

  useActiveWorkspaceSubscriber(
    feed,
    event => {
      switch (event.type) {
        case 'quote-holdings:update':
          throttledCallback(feed.getCachedValue());
          break;
      }
    },
    fields,
  );

  const addSymbolsToFeed = (symbols: Security[]) => {
    feed.addSymbols(symbols);
  };

  const getSymbolsFromFeed = () => {
    return feed.getSymbols();
  };

  const removeSymbolsFromFeed = (symbols: Security[]) => {
    feed.removeSymbols(symbols);
  };

  return {
    addSymbolsToFeed,
    getSymbolsFromFeed,
    removeSymbolsFromFeed,
  };
};
